import { useState } from 'react';
import { useResource } from '../lib/useResource.js';
import { post } from '../lib/api.js';
import { formatDate, formatDateTime, formatTime, todayLocal } from '../lib/format.js';
import {
  Alert,
  Badge,
  Card,
  Empty,
  ErrorBox,
  Field,
  Modal,
  PageHeader,
  useSubmit,
  useToast,
} from '../components/ui.js';
import { Tabela } from '../components/Tabela.js';
import { useSession } from '../lib/session.js';

interface Booking {
  id: string;
  customer_id: string;
  status: string;
  starts_at: string;
  ends_at: string;
  notes: string | null;
}
interface Slot {
  startsAt: string;
  endsAt: string;
  resourceId: string;
  resourceName: string;
  label: string;
}
interface Resource {
  id: string;
  name: string;
  active: boolean;
}
interface CatalogItem {
  id: string;
  name: string;
  type: string;
}
interface Customer {
  id: string;
  name: string;
}

const STATUS: Record<string, { label: string; tone: 'neutral' | 'attention' | 'critical' | 'ok' }> = {
  requested: { label: 'reserva temporária', tone: 'attention' },
  confirmed: { label: 'confirmada', tone: 'ok' },
  in_service: { label: 'em atendimento', tone: 'ok' },
  completed: { label: 'concluída', tone: 'neutral' },
  cancelled: { label: 'cancelada', tone: 'neutral' },
  no_show: { label: 'não compareceu', tone: 'critical' },
};

function addDays(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number);
  const dt = new Date(Date.UTC(y!, m! - 1, d!));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

export function Agenda() {
  const { can } = useSession();
  const toast = useToast();
  const [from, setFrom] = useState(todayLocal());
  const to = addDays(from, 6);
  const [novo, setNovo] = useState(false);

  const bookings = useResource<{ bookings: Booking[] }>(
    `/api/bookings?fromDate=${from}&toDate=${to}`,
    [from],
  );
  const clientes = useResource<{ customers: Customer[] }>('/api/customers?term=');

  const nomePorCliente = new Map((clientes.data?.customers ?? []).map((c) => [c.id, c.name]));

  return (
    <>
      <PageHeader
        title="Agenda"
        description="Sobreposição é impedida pela aplicação e pelo banco. Confirmação ao cliente só depois da reserva gravada."
        actions={
          can('bookings.write') ? (
            <button type="button" className="primary" onClick={() => setNovo(true)}>
              Nova reserva
            </button>
          ) : null
        }
      />

      <div className="toolbar">
        <button type="button" onClick={() => setFrom(addDays(from, -7))}>
          Semana anterior
        </button>
        <button type="button" onClick={() => setFrom(todayLocal())}>
          Hoje
        </button>
        <button type="button" onClick={() => setFrom(addDays(from, 7))}>
          Próxima semana
        </button>
        <span className="muted small">
          {formatDate(from)} a {formatDate(to)}
        </span>
      </div>

      <ErrorBox error={bookings.error} />

      <Card tight>
        <Tabela
          linhas={bookings.data?.bookings ?? []}
          chave={(b) => b.id}
          vazio="Nenhuma reserva neste período."
          colunas={[
            {
              chave: 'cliente',
              rotulo: 'Cliente',
              tipo: 'principal',
              render: (b) => nomePorCliente.get(b.customer_id) ?? '—',
            },
            { chave: 'data', rotulo: 'Data', render: (b) => formatDate(b.starts_at) },
            {
              chave: 'horario',
              rotulo: 'Horário',
              render: (b) => (
                <span className="mono">
                  {formatTime(b.starts_at)} – {formatTime(b.ends_at)}
                </span>
              ),
            },
            {
              chave: 'situacao',
              rotulo: 'Situação',
              render: (b) => (
                <Badge tone={STATUS[b.status]?.tone ?? 'neutral'}>
                  {STATUS[b.status]?.label ?? b.status}
                </Badge>
              ),
            },
            {
              chave: 'acoes',
              rotulo: '',
              tipo: 'actions',
              render: (b) =>
                can('bookings.write') && ['requested', 'confirmed'].includes(b.status) ? (
                  <>
                    <button
                      type="button"
                      className="small"
                      onClick={async () => {
                        await post(`/api/bookings/${b.id}/status`, { status: 'completed' });
                        bookings.reload();
                        toast('Reserva concluída.');
                      }}
                    >
                      Concluir
                    </button>
                    <button
                      type="button"
                      className="small danger"
                      onClick={async () => {
                        await post(`/api/bookings/${b.id}/status`, {
                          status: 'cancelled',
                          reason: 'cancelada no painel',
                        });
                        bookings.reload();
                        toast('Reserva cancelada. Nenhum lembrete foi retomado automaticamente.');
                      }}
                    >
                      Cancelar
                    </button>
                  </>
                ) : null,
            },
          ]}
        />
      </Card>

      {novo ? (
        <NovaReserva
          onClose={() => setNovo(false)}
          onCreated={() => {
            setNovo(false);
            bookings.reload();
            toast('Reserva confirmada.');
          }}
        />
      ) : null}
    </>
  );
}

function NovaReserva({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [customerId, setCustomerId] = useState('');
  const [itemId, setItemId] = useState('');
  const [fromDate, setFromDate] = useState(todayLocal());
  const [slot, setSlot] = useState<Slot | null>(null);

  const clientes = useResource<{ customers: Customer[] }>('/api/customers?term=');
  const servicos = useResource<{ items: CatalogItem[] }>('/api/catalog/search?limit=100');
  const recursos = useResource<{ resources: Resource[] }>('/api/resources');
  const slots = useResource<{ slots: Slot[] }>(
    itemId ? `/api/bookings/slots?fromDate=${fromDate}&toDate=${addDays(fromDate, 6)}&itemId=${itemId}` : null,
    [itemId, fromDate],
  );

  const { submit, saving, error } = useSubmit(async () => {
    if (!slot) throw new Error('Escolha um horário disponível.');
    await post('/api/bookings', {
      customerId,
      resourceId: slot.resourceId,
      itemId,
      startsAt: slot.startsAt,
    });
    onCreated();
  });

  const apenasServicos = (servicos.data?.items ?? []).filter((i) => i.type === 'service');

  return (
    <Modal
      title="Nova reserva"
      wide
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" form="form-reserva" className="primary" disabled={saving || !slot}>
            {saving ? 'Gravando…' : 'Confirmar reserva'}
          </button>
        </>
      }
    >
      <form id="form-reserva" onSubmit={submit}>
        <ErrorBox error={error} />
        {recursos.data && recursos.data.resources.filter((r) => r.active).length === 0 ? (
          <Alert tone="warning" title="Nenhum recurso de agenda ativo">
            Cadastre um box ou técnico em Configurações antes de agendar.
          </Alert>
        ) : null}

        <div className="field-row">
          <Field label="Cliente" required>
            <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} required>
              <option value="">Escolha o cliente</option>
              {(clientes.data?.customers ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Serviço" required hint="Só serviços com duração configurada aparecem aqui.">
            <select
              value={itemId}
              onChange={(e) => {
                setItemId(e.target.value);
                setSlot(null);
              }}
              required
            >
              <option value="">Escolha o serviço</option>
              {apenasServicos.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="A partir de">
            <input
              type="date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setSlot(null);
              }}
            />
          </Field>
        </div>

        <ErrorBox error={slots.error} />

        {itemId ? (
          slots.data && slots.data.slots.length === 0 ? (
            <Empty title="Nenhum horário livre nesse período." />
          ) : (
            <div className="row" style={{ gap: 8 }}>
              {(slots.data?.slots ?? []).slice(0, 24).map((s) => (
                <button
                  key={`${s.resourceId}-${s.startsAt}`}
                  type="button"
                  className={slot?.startsAt === s.startsAt && slot?.resourceId === s.resourceId ? 'small primary' : 'small'}
                  onClick={() => setSlot(s)}
                >
                  {s.label} · {s.resourceName}
                </button>
              ))}
            </div>
          )
        ) : null}

        {slot ? (
          <Alert tone="info" title="Resumo da reserva">
            {formatDateTime(slot.startsAt)} em {slot.resourceName}. A confirmação ao cliente só é
            enviada depois que a reserva estiver gravada.
          </Alert>
        ) : null}
      </form>
    </Modal>
  );
}
